/**
 * ECIES-X25519-AES-GCM encryption for device ID payload.
 * Matches backend encryption format (Node.js crypto / Go).
 */
import { x25519 } from '@noble/curves/ed25519';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha2';
import { gcm } from '@noble/ciphers/aes';
import { randomBytes } from '@noble/ciphers/utils';

const ECIES_NONCE_LEN = 12;
const ECIES_HKDF_INFO = 'ECIES-X25519-AES-GCM';

function decodePublicKey(pubKeyBase64) {
  const decoded = Uint8Array.from(
    atob(pubKeyBase64.replace(/\s/g, '')),
    (c) => c.charCodeAt(0)
  );
  return decoded.length >= 32 ? decoded.slice(0, 32) : decoded;
}

export async function encryptWithECIES(plainText, publicKeyBase64) {
  const recipientPubBytes = decodePublicKey(publicKeyBase64);

  // Ephemeral keypair
  const ephemeralPriv = x25519.utils.randomSecretKey();
  const ephemeralPub = x25519.getPublicKey(ephemeralPriv);

  // Shared secret via X25519 ECDH
  const sharedSecret = x25519.getSharedSecret(ephemeralPriv, recipientPubBytes);

  // HKDF -> AES key (empty salt to match backend)
  const aesKey = hkdf(
    sha256,
    sharedSecret,
    new Uint8Array(0),
    new TextEncoder().encode(ECIES_HKDF_INFO),
    32
  );

  // AES-256-GCM
  const nonce = randomBytes(ECIES_NONCE_LEN);
  const aes = gcm(aesKey, nonce);
  const plainBytes = new TextEncoder().encode(plainText);
  const ciphertextWithTag = aes.encrypt(plainBytes);

  // Output: ephPub(32) || nonce(12) || ciphertext || tag(16)
  const out = new Uint8Array(32 + 12 + ciphertextWithTag.length);
  out.set(ephemeralPub, 0);
  out.set(nonce, 32);
  out.set(ciphertextWithTag, 44);

  return btoa(String.fromCharCode.apply(null, out));
}
